import React, { useContext, useState, useEffect, useRef } from 'react';
import { Card, Typography, Radio, Select, Divider, Button, message, Input, InputNumber, Row, Col } from 'antd';
import { CarritoContext } from '../../../../context/carritoContext';
import { LoginContext } from '../../../../context/loginContext';
import { GlobalContext } from '../../../../context/globalContext';
import { SucursalContext } from '../../../../context/sucursalContext';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

/** HH:mm para la leyenda (string desde API, ISO o Date). */
function formatHoraLeyenda(val) {
  if (val == null || val === '') return '';
  if (typeof val === 'string') {
    const t = val.trim();
    const timeOnly = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
    if (timeOnly && !t.includes('T')) {
      return `${timeOnly[1].padStart(2, '0')}:${timeOnly[2]}`;
    }
    if (t.includes('T')) {
      const d = new Date(t);
      if (!Number.isNaN(d.getTime())) {
        return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
      }
    }
    return t;
  }
  if (val instanceof Date) {
    return `${String(val.getUTCHours()).padStart(2, '0')}:${String(val.getUTCMinutes()).padStart(2, '0')}`;
  }
  return String(val);
}

/** Detecta tipo de pago "tarjeta de crédito" por el nombre (ignora tildes). */
function esTarjetaCreditoPorNombre(nombreTipo) {
  if (!nombreTipo) return false;
  const n = nombreTipo.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
  return n.includes('tarjeta') && n.includes('credito');
}

/** Detecta cuenta corriente aunque el nombre venga con/sin tildes o espacios raros */
function esCuentaCorrientePorNombre(nombreTipo) {
  if (!nombreTipo) return false;
  const n = nombreTipo.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
  return n.includes('cuenta') && n.includes('corriente');
}

const CFinalizarCarrito = () => {
  const { carrito, fetchCarrito } = useContext(CarritoContext);
  const { user } = useContext(LoginContext);
  const { tipoPagos, criteriosReemplazo } = useContext(GlobalContext);
  const { sucursal, seleccionarSucursal } = useContext(SucursalContext);
  const [direccionSeleccionada, setDireccionSeleccionada] = useState(null);
  const [retiraEnLocal, setRetiraEnLocal] = useState(false);
  const [metodoPago, setMetodoPago] = useState(null);
  const [numeroCuentaCorriente, setNumeroCuentaCorriente] = useState('');
  const [tarjetaNombre, setTarjetaNombre] = useState('');
  const [tarjetaBanco, setTarjetaBanco] = useState('');
  const [tarjetaCuotas, setTarjetaCuotas] = useState(null);
  const [criterioSeleccionado, setCriterioSeleccionado] = useState(null);
  const [productosSolicitados, setProductosSolicitados] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const cuentaCorrienteInputRef = useRef(null);

  const tipoPagoPorId = (id) =>
    tipoPagos?.find((tp) => String(tp.tpa_id) === String(id ?? ''));

  // Datos de sucursal en localStorage pueden quedar viejos si se editaron horarios en el panel
  useEffect(() => {
    const id = carrito?.car_sucursal ?? sucursal?.suc_id;
    if (!id) return;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_API_URL}/sucursal/${id}`)
      .then((r) => {
        if (!r.ok) throw new Error('sucursal');
        return r.json();
      })
      .then((data) => {
        if (!cancelled && data?.suc_id) {
          seleccionarSucursal(data);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [carrito?.car_sucursal, sucursal?.suc_id, seleccionarSucursal]);

  const hoy = new Date();

  const calcularPrecioUnitario = (item) => {
    const desde = item.asu_fecha_d_descuento ? new Date(item.asu_fecha_d_descuento) : null;
    const hasta = item.asu_fecha_h_descuento ? new Date(item.asu_fecha_h_descuento) : null;
    const enDescuento =
      desde && hasta &&
      hoy >= desde &&
      hoy <= hasta &&
      item.asu_precio_con_descuento !== null;

    return enDescuento
      ? Number(item.asu_precio_con_descuento)
      : Number(item.asu_precioventa);
  };

  // Función auxiliar para calcular el subtotal de un item (igual que en CCarritoCliente)
  const calcularSubtotal = (item) => {
    const tipoUnidadNombre = item.tipo_unidad_nombre || 'Entero';
    const esFraccionado = tipoUnidadNombre.toLowerCase() === 'fraccionado' && item.contenido_neto;
    const contenidoNeto = item.contenido_neto ? parseFloat(item.contenido_neto) : null;
    const unidadMedida = (item.ums_nombre || '').toLowerCase().trim();
    
    const esGramos = esFraccionado && (
      unidadMedida.includes('gramo') || 
      unidadMedida === 'g' || 
      unidadMedida === 'gr' ||
      unidadMedida === 'gramos' ||
      (!unidadMedida && contenidoNeto && contenidoNeto >= 100)
    ) && !unidadMedida.includes('kilo');
    
    const esKilos = esFraccionado && (
      unidadMedida.includes('kilo') || 
      unidadMedida === 'kg' ||
      unidadMedida === 'kilogramo' ||
      unidadMedida === 'kilogramos' ||
      (!unidadMedida && contenidoNeto && contenidoNeto < 10 && contenidoNeto > 0)
    );
    
    const precioFinal = calcularPrecioUnitario(item);
    const cantidadNum = parseFloat(item.dec_cantidad) || 0;
    
    if (esFraccionado && (esGramos || esKilos)) {
      // El precio unitario es por kilo
      // Convertir la cantidad a kilos para el cálculo (igual que en la visualización)
      let cantidadEnKilos;
      
      if (esGramos) {
        // Convertir de gramos a kilos
        cantidadEnKilos = cantidadNum / 1000;
      } else {
        // Ya está en kilos
        cantidadEnKilos = cantidadNum;
      }
      
      // Subtotal = precio por kilo × cantidad en kilos
      return precioFinal * cantidadEnKilos;
    } else {
      // Para artículos enteros, calcular normalmente
      // El precio es por unidad, así que multiplicamos por la cantidad
      return cantidadNum * precioFinal;
    }
  };

  const totalFinal = carrito?.detalles?.reduce(
    (sum, item) => sum + calcularSubtotal(item),
    0
  );

  const costoEnvio = !retiraEnLocal ? Number(sucursal?.suc_costoenvio || 0) : 0;
  const totalConEnvio = totalFinal + costoEnvio;

  const direcciones = user?.direcciones || [];

  // Verificar si el método de pago seleccionado es cuenta corriente
  const metodoPagoSeleccionado = tipoPagoPorId(metodoPago);
  const esCuentaCorriente = esCuentaCorrientePorNombre(metodoPagoSeleccionado?.tpa_nombre);
  const esTarjetaCredito = esTarjetaCreditoPorNombre(metodoPagoSeleccionado?.tpa_nombre);

  // Tras cerrar el Select en móvil, el foco a veces no llega al input; re-enfocar con un pequeño delay evita que "saque" del campo.
  useEffect(() => {
    if (!esCuentaCorriente) return undefined;
    const t = window.setTimeout(() => {
      const ref = cuentaCorrienteInputRef.current;
      if (!ref?.focus) return;
      try {
        ref.focus({ preventScroll: true });
      } catch {
        ref.focus();
      }
    }, 320);
    return () => window.clearTimeout(t);
  }, [esCuentaCorriente, metodoPago]);

  const faltanCampos = {
    direccion: !retiraEnLocal && !direccionSeleccionada,
    metodoPago: !metodoPago,
    cuentaCorriente: esCuentaCorriente && !numeroCuentaCorriente.trim(),
    tarjetaCredito:
      esTarjetaCredito &&
      (!tarjetaNombre.trim() ||
        !tarjetaBanco.trim() ||
        tarjetaCuotas == null ||
        Number(tarjetaCuotas) < 1),
    criterio: !criterioSeleccionado
  };

  const handleFinalizarPedido = async () => {
    if (isSubmitting) return;

    const direccion_id = retiraEnLocal ? 0 : direccionSeleccionada;
    const tipo_envio = retiraEnLocal ? 2 : 1;

    const payload = {
      cli_id: user.cliente.cli_id,
      suc_id: carrito.car_sucursal,
      direccion_id,
      forma_pago: metodoPago,
      tipo_envio,
      total: totalConEnvio.toFixed(2),
      criterio_reemplazo: criterioSeleccionado,
      ...(esCuentaCorriente && numeroCuentaCorriente && { numero_cuenta_corriente: numeroCuentaCorriente.trim() }),
      ...(esTarjetaCredito && {
        tarjeta_nombre: tarjetaNombre.trim(),
        tarjeta_banco: tarjetaBanco.trim(),
        tarjeta_cuotas: tarjetaCuotas
      }),
      ...(productosSolicitados.trim() && { productos_solicitados: productosSolicitados.trim() }),
      ...(observaciones.trim() && { observaciones: observaciones.trim() })
    };

    try {
      setIsSubmitting(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/pedido`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        fetchCarrito(); // limpiar carrito

        // if (data.mpInitPoint && metodoPago === 2) {
        //   window.location.href = data.mpInitPoint;
        //   return;
        // }

        message.success('Pedido confirmado con éxito');
        navigate('/home');
      } else {
        message.error(data?.message || 'Error al procesar el pedido');
      }

    } catch (error) {
      console.error('Error al confirmar pedido:', error);
      message.error('Error al procesar el pedido');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 12, maxWidth: 800, margin: '0 auto' }}>
      {/* Sección 1: Resumen del carrito */}
      <Card title="Resumen del carrito" style={{ marginBottom: 24 }}>
        {carrito?.detalles.map(item => {
          const precioUnitario = calcularPrecioUnitario(item);
          const tieneDescuento = precioUnitario !== Number(item.asu_precioventa);
          const totalItem = calcularSubtotal(item);

          return (
            <div key={item.dec_id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <Text strong>{item.aem_nombre}</Text> <br />
                <Text type="secondary">x{item.dec_cantidad}</Text>
                {tieneDescuento && (
                  <Text type="danger" style={{ marginLeft: 8 }}>(¡Con descuento!)</Text>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                {tieneDescuento && (() => {
                  // Calcular el subtotal sin descuento para mostrar el precio tachado
                  const tipoUnidadNombre = item.tipo_unidad_nombre || 'Entero';
                  const esFraccionado = tipoUnidadNombre.toLowerCase() === 'fraccionado' && item.contenido_neto;
                  const contenidoNeto = item.contenido_neto ? parseFloat(item.contenido_neto) : null;
                  const unidadMedida = (item.ums_nombre || '').toLowerCase().trim();
                  
                  const esGramos = esFraccionado && (
                    unidadMedida.includes('gramo') || 
                    unidadMedida === 'g' || 
                    unidadMedida === 'gr' ||
                    unidadMedida === 'gramos' ||
                    (!unidadMedida && contenidoNeto && contenidoNeto >= 100)
                  ) && !unidadMedida.includes('kilo');
                  
                  const esKilos = esFraccionado && (
                    unidadMedida.includes('kilo') || 
                    unidadMedida === 'kg' ||
                    unidadMedida === 'kilogramo' ||
                    unidadMedida === 'kilogramos' ||
                    (!unidadMedida && contenidoNeto && contenidoNeto < 10 && contenidoNeto > 0)
                  );
                  
                  const precioSinDescuento = Number(item.asu_precioventa);
                  const cantidadNum = parseFloat(item.dec_cantidad) || 0;
                  
                  let subtotalSinDescuento;
                  if (esFraccionado && (esGramos || esKilos)) {
                    let cantidadEnKilos;
                    if (esGramos) {
                      cantidadEnKilos = cantidadNum / 1000;
                    } else {
                      cantidadEnKilos = cantidadNum;
                    }
                    subtotalSinDescuento = precioSinDescuento * cantidadEnKilos;
                  } else {
                    subtotalSinDescuento = cantidadNum * precioSinDescuento;
                  }
                  
                  return (
                    <div style={{ fontSize: 12 }}>
                      <Text delete style={{ color: '#888' }}>
                        ${subtotalSinDescuento.toFixed(2)}
                      </Text>
                    </div>
                  );
                })()}
                <div style={{ fontWeight: 500 }}>
                  ${totalItem.toFixed(2)}
                </div>
              </div>
            </div>
          );
        })}

        <Divider />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Text>Subtotal:</Text>
          <Text>${totalFinal.toFixed(2)}</Text>
        </div>

        {!retiraEnLocal && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text>Envío:</Text>
            <Text>${costoEnvio.toFixed(2)}</Text>
          </div>
        )}

        <Divider />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Text strong>Total:</Text>
          <Text strong>${totalConEnvio.toFixed(2)}</Text>
        </div>
      </Card>

      {/* Sección 2: Productos no encontrados */}
      <Card title="¿No encontraste tu producto?" style={{ marginBottom: 24 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          A continuación detallalo, si lo tenemos te lo enviamos.
        </Text>
        <Input.TextArea
          rows={4}
          placeholder="Ej: 1 kg de yerba, 2 litros de leche descremada..."
          value={productosSolicitados}
          onChange={(e) => setProductosSolicitados(e.target.value)}
          maxLength={2000}
          showCount
        />
      </Card>

      {/* Sección 3: Observaciones */}
      <Card title="Observaciones" style={{ marginBottom: 24 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Indicá lo que necesites, por ejemplo: casa portón blanco.
        </Text>
        <Input.TextArea
          rows={3}
          placeholder="Ej: casa portón blanco, timbre roto, dejar en portería..."
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          maxLength={1000}
          showCount
        />
      </Card>

      {/* Sección 4: Dirección de entrega */}
      <Card title="Dirección de entrega" style={{ marginBottom: 24 }}>
        {sucursal?.suc_horarioenviod && sucursal?.suc_horarioenvioh && (
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Los pedidos realizados hasta las {formatHoraLeyenda(sucursal.suc_horarioenviod)} serán entregados por la mañana.
            </Text>
            <Text strong style={{ display: 'block' }}>
              Los pedidos realizados hasta las {formatHoraLeyenda(sucursal.suc_horarioenvioh)} serán entregados por la tarde.
            </Text>
          </div>
        )}

        <Radio.Group
          onChange={(e) => {
            const valor = e.target.value;
            setRetiraEnLocal(valor === 'local');
            setDireccionSeleccionada(null);
          }}
          value={retiraEnLocal ? 'local' : 'domicilio'}
          style={{ marginBottom: 16 }}
        >
          <Radio value="domicilio">Enviar a domicilio</Radio>
          <Radio value="local">Retiro en local</Radio>
        </Radio.Group>

        {!retiraEnLocal && (
          <>
            <Radio.Group
              onChange={(e) => setDireccionSeleccionada(e.target.value)}
              value={direccionSeleccionada}
              style={{ width: '100%' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {direcciones.map((dir) => (
                  <Radio
                    key={dir.dci_id}
                    value={dir.dci_id}
                    style={{
                      padding: 12,
                      border: '1px solid #d9d9d9',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'flex-start',
                      backgroundColor: direccionSeleccionada === dir.dci_id ? '#f5f8ff' : '#fff',
                      fontSize: 16,
                      fontWeight: direccionSeleccionada === dir.dci_id ? 'bold' : 500
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{dir.dci_direccion}</div>
                      <div style={{ fontSize: 14, color: '#777' }}>
                        CP: {dir.dci_codigoposta} – {dir.dci_localidad}
                        {dir.dci_observacion ? ` – ${dir.dci_observacion}` : ''}
                        {user?.usu_nombre && user?.usu_telefono && (
                          <>
                            {'  |  '}
                            {user.usu_nombre} - {user.usu_telefono}
                          </>
                        )}
                      </div>
                    </div>
                  </Radio>
                ))}
              </div>
            </Radio.Group>

            {direcciones.length === 0 && (
              <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  onClick={() => navigate('/datos-cliente')}
                >
                  Registrar una dirección
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Sección 5: Forma de pago */}
      <Card title="Forma de pago" style={{ marginBottom: 24 }}>
        <Select
          style={{ width: '100%', marginBottom: esCuentaCorriente || esTarjetaCredito ? 16 : 0 }}
          placeholder="Seleccione un método de pago"
          value={metodoPago}
          virtual={false}
          getPopupContainer={(trigger) => trigger.parentElement || document.body}
          onChange={(value) => {
            setMetodoPago(value);
            const tp = tipoPagoPorId(value);
            const nombre = tp?.tpa_nombre || '';
            if (!esCuentaCorrientePorNombre(nombre)) {
              setNumeroCuentaCorriente('');
            }
            if (!esTarjetaCreditoPorNombre(nombre)) {
              setTarjetaNombre('');
              setTarjetaBanco('');
              setTarjetaCuotas(null);
            }
          }}
        >
          {tipoPagos?.map(tp => (
            <Option key={tp.tpa_id} value={tp.tpa_id}>
              {tp.tpa_nombre}
            </Option>
          ))}
        </Select>
        {esCuentaCorriente && (
          <div style={{ marginTop: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Número de cuenta corriente:
            </Text>
            <Input
              ref={cuentaCorrienteInputRef}
              name="numero_cuenta_corriente_pedido"
              id="numero-cuenta-corriente-pedido"
              placeholder="Ingrese el número de cuenta corriente"
              value={numeroCuentaCorriente}
              onChange={(e) => setNumeroCuentaCorriente(e.target.value)}
              style={{ width: '100%' }}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              inputMode="numeric"
            />
          </div>
        )}
        {esTarjetaCredito && (
          <div style={{ marginTop: esCuentaCorriente ? 16 : 0 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Datos de tarjeta de crédito
            </Text>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  Nombre en la tarjeta
                </Text>
                <Input
                  placeholder="Nombre"
                  value={tarjetaNombre}
                  onChange={(e) => setTarjetaNombre(e.target.value)}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  Banco
                </Text>
                <Input
                  placeholder="Nombre del banco"
                  value={tarjetaBanco}
                  onChange={(e) => setTarjetaBanco(e.target.value)}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  Cuotas
                </Text>
                <InputNumber
                  min={1}
                  max={48}
                  placeholder="Cantidad"
                  value={tarjetaCuotas}
                  onChange={setTarjetaCuotas}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>
          </div>
        )}
      </Card>

      {/* Sección 6: Criterio de Reemplazo */}
      <Card title="Criterio de reemplazo" style={{ marginBottom: 24 }}>
        <Select
          style={{ width: '100%' }}
          placeholder="Seleccione un criterio de reemplazo"
          value={criterioSeleccionado}
          onChange={setCriterioSeleccionado}
          virtual={false}
          getPopupContainer={(trigger) => trigger.parentElement || document.body}
        >
          {criteriosReemplazo.map((c) => (
            <Option key={c.cre_id} value={c.cre_id}>
              {c.cre_descripcion}
            </Option>
          ))}
        </Select>
      </Card>

      <div style={{ textAlign: 'right' }}>
        <Button
          type="primary"
          size="large"
          onClick={handleFinalizarPedido}
          loading={isSubmitting}
          disabled={
            isSubmitting ||
            faltanCampos.direccion ||
            faltanCampos.metodoPago ||
            faltanCampos.cuentaCorriente ||
            faltanCampos.tarjetaCredito ||
            faltanCampos.criterio
          }
        >
          Confirmar pedido
        </Button>

        {(faltanCampos.direccion ||
          faltanCampos.metodoPago ||
          faltanCampos.cuentaCorriente ||
          faltanCampos.tarjetaCredito ||
          faltanCampos.criterio) && (
          <div style={{ marginTop: 8, textAlign: 'left' }}>
            <Text type="danger">
              {faltanCampos.direccion && 'Debe seleccionar una dirección de envío. '}
              {faltanCampos.metodoPago && 'Debe seleccionar una forma de pago. '}
              {faltanCampos.cuentaCorriente && 'Debe ingresar el número de cuenta corriente. '}
              {faltanCampos.tarjetaCredito &&
                'Debe completar nombre en la tarjeta, banco y cantidad de cuotas. '}
              {faltanCampos.criterio && 'Debe seleccionar un criterio de reemplazo.'}
            </Text>
          </div>
        )}
      </div>

    </div>
  );
};

export default CFinalizarCarrito;